import { Building2, CircleAlert, Ellipsis, Link2, Lock, Plus, ShieldCheck, Trash2, UserRound, Users } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { FormEvent } from 'react';
import { ROLE_RANK, roleAtLeast } from '@orgus/protocol';
import type { AccessRule, AuthState, LinkedIdentity, Member, MembersResponse, Role, SignInSettings } from '@orgus/protocol';
import { ApiError } from '../../lib/api.ts';
import { useExt } from '../../lib/store.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import {
  Avatar,
  BUTTON_SIZE,
  BUTTON_VARIANT,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  SkeletonRows,
  StatusDot,
  StatusPill,
  Tooltip,
  cn,
  toast,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { authClient } from './client.ts';
import { InlineChoice } from '../detail/InlineEdit.tsx';
import { IdentityMarks } from './IdentityChips.tsx';
import { InvitesPanel, LinkInviteDialog } from './InvitesPanel.tsx';
import { ROLE_LABEL, ROLE_VALUE_CLASS, grantableRoles, memberHandle, providerStatusOf, roleOptions, viaLabel } from './labels.ts';
import { RemovedPanel } from './RemovedPanel.tsx';
import { useMe } from './useMe.ts';

/** An older hub has no /api/auth/sign-in (404): show members without linked accounts. Other errors surface. */
const orNullIfMissing = (e: unknown): null => {
  if (e instanceof ApiError && e.status === 404) return null;
  throw e;
};
import { useResource } from './useResource.ts';

function RoleValue({ role }: { role: Role }) {
  return <span className={ROLE_VALUE_CLASS}>{ROLE_LABEL[role]}</span>;
}

/** GitHub sign-in itself is set up in Settings, Sign-in; the allow list only shows whether it is on. */
const githubStatus = (data: MembersResponse) => {
  const st = providerStatusOf({ configured: data.github.configured, needsAttention: false, testIssuer: false });
  return { status: st.status, label: `GitHub sign-in ${st.label.toLowerCase()}` };
};

function AddRuleForm({ myRole, onAdded }: { myRole: Role; onAdded: () => void }) {
  const [kind, setKind] = useState<'user' | 'org'>('user');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim().replace(/^@/, '');
    if (!/^[a-z0-9](?:[a-z0-9-]{0,38})$/i.test(n)) {
      setError(kind === 'user' ? 'Use the GitHub login as it appears in the profile URL, for example octocat.' : 'Use the organisation name as it appears in its URL, for example my-org.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authClient.setRule(kind, n, role);
      toast.success(`${n} can sign in`, `${kind === 'org' ? 'Members of the organisation' : 'They'} get the ${ROLE_LABEL[role]} role.`);
      setName('');
      onAdded();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-start gap-2 border-b border-border-subtle p-3" noValidate>
      <Segmented
        label="Who to allow"
        value={kind}
        onChange={(v) => {
          setKind(v);
          setError(null);
        }}
        options={[
          { value: 'user', label: 'GitHub user', icon: <UserRound /> },
          { value: 'org', label: 'Organisation', icon: <Building2 /> },
        ]}
      />
      <Input
        size="sm"
        aria-label={kind === 'user' ? 'GitHub login' : 'GitHub organisation'}
        placeholder={kind === 'user' ? 'GitHub login, for example octocat' : 'Organisation, for example my-org'}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={error ?? undefined}
        wrapperClassName="min-w-[240px] flex-1"
        autoComplete="off"
        spellCheck={false}
      />
      <Select size="sm" aria-label="Role" value={role} onValueChange={(v) => setRole(v as Role)} options={roleOptions(grantableRoles(myRole))} />
      <Button type="submit" size="sm" variant="primary" icon={<Plus />} loading={busy}>
        Allow
      </Button>
    </form>
  );
}

function RulesPanel({ data, myRole, members, onChanged }: { data: MembersResponse; myRole: Role; members: Member[]; onChanged: () => void }) {
  const [removing, setRemoving] = useState<AccessRule | null>(null);
  const signedIn = useMemo(() => new Set(members.map((m) => m.login.toLowerCase())), [members]);
  const rules = useMemo(() => [...data.rules].sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'user' ? -1 : 1)), [data.rules]);
  const columns: Column<AccessRule>[] = [
    {
      key: 'name',
      header: 'Who',
      grow: true,
      text: (r) => r.name,
      sortValue: (r) => r.name.toLowerCase(),
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2.5">
          {r.kind === 'org' ? <Building2 className="size-4 shrink-0 text-fg-subtle" aria-label="Organisation" /> : <UserRound className="size-4 shrink-0 text-fg-subtle" aria-label="GitHub user" />}
          <span className="truncate font-medium text-fg">{r.name}</span>
          {r.kind === 'org' && <span className="hidden shrink-0 text-sm text-fg-subtle sm:inline">every member</span>}
        </span>
      ),
    },
    { key: 'role', header: 'Role', type: 'badge', sortValue: (r) => ROLE_RANK[r.role], cell: (r) => <RoleValue role={r.role} /> },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'md',
      cell: (r) =>
        r.kind === 'org' ? (
          <span className="text-sm text-fg-subtle">{members.filter((m) => m.via === `org:${r.name}`).length} signed in</span>
        ) : signedIn.has(r.name.toLowerCase()) ? (
          <span className="flex items-center gap-1.5 text-sm text-fg-muted">
            <StatusDot tone="passed" label="" /> Signed in
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-sm text-fg-muted">
            <StatusDot tone="queued" label="" /> Not signed in yet
          </span>
        ),
    },
    {
      key: 'source',
      header: 'Added',
      hideBelow: 'lg',
      cell: (r) =>
        r.source === 'config' ? (
          <Tooltip content="Set in auth.json on the hub. Edit that file to change it.">
            <span className="inline-flex items-center gap-1.5 text-sm text-fg-subtle" tabIndex={0}>
              <Lock className="size-3.5" aria-hidden /> auth.json
            </span>
          </Tooltip>
        ) : (
          <span className="text-sm text-fg-subtle">
            {r.addedBy ?? 'Settings'}, <RelativeTime ts={r.addedAt} />
          </span>
        ),
    },
  ];
  return (
    <Panel
      title="GitHub allow list"
      sub={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`}
      icon={<ShieldCheck />}
      flush
    >
      {/* Whether GitHub sign-in is on, and where it is set up: one quiet line, so the title keeps its room on a phone. */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <StatusPill variant="plain" {...githubStatus(data)} />
        <Link to="/settings/sign-in" className={cn(BUTTON_VARIANT.ghost, BUTTON_SIZE.sm)}>
          Open sign-in settings
        </Link>
      </div>
      <AddRuleForm myRole={myRole} onAdded={onChanged} />
      <DataTable
        frameless
        label="Allow list"
        rows={rules}
        rowKey={(r) => `${r.source}:${r.kind}:${r.name}`}
        columns={columns}
        pageSize={false}
        empty={
          <EmptyState
            icon={<ShieldCheck />}
            title="Nobody can sign in with GitHub yet"
            description="Allow a GitHub user or organisation above, with the role they get."
          />
        }
        rowActions={(r) =>
          r.source === 'settings' && (r.role !== 'owner' || myRole === 'owner') ? (
            <IconButton label={`Remove ${r.name} from the allow list`} icon={<Trash2 />} variant="ghost" size="sm" onClick={() => setRemoving(r)} />
          ) : null
        }
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? `Remove ${removing.name} from the allow list?` : ''}
        description={
          removing?.kind === 'org'
            ? 'Members who got in only through this organisation are signed out now and their tokens are revoked.'
            : 'If they are signed in, they are signed out now and their tokens are revoked, unless an organisation rule still lets them in.'
        }
        confirmLabel="Remove"
        onConfirm={async () => {
          if (!removing) return;
          await authClient.removeRule(removing.kind, removing.name);
          toast.success(`${removing.name} was removed from the allow list`);
          setRemoving(null);
          onChanged();
        }}
      />
    </Panel>
  );
}

/** True when `myRole` may change or remove a member with role `theirs` (owners for owners). */
const canManageRole = (myRole: Role, theirs: Role) => roleAtLeast(myRole, 'admin') && (theirs !== 'owner' || myRole === 'owner');

export function MembersPanel({
  data,
  identities,
  myRole,
  myLogin,
  canLinkInvite,
  onChanged,
}: {
  data: MembersResponse;
  /** Linked Google and Microsoft accounts by member id; empty when the hub does not have them. */
  identities: Record<string, LinkedIdentity[]>;
  myRole: Role;
  myLogin: string | null;
  /** The hub offers Google or Microsoft, so a sign-in can be added through a link. */
  canLinkInvite: boolean;
  onChanged: () => void;
}) {
  const [removing, setRemoving] = useState<Member | null>(null);
  const [adding, setAdding] = useState<Member | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  // Below 768 px the row is two lines (name; handle and role) and its actions are one menu.
  const narrow = useMediaQuery(NARROW_QUERY);
  const canEdit = (m: Member) => data.canManage && m.provider !== 'owner-link' && m.login !== myLogin && canManageRole(myRole, m.role);
  // Owners for admin and owner targets; never the bootstrap owner (it links from Settings, Sign-in).
  const canAddSignIn = (m: Member) => canLinkInvite && data.canManage && m.provider !== 'owner-link' && (roleAtLeast(m.role, 'admin') ? myRole === 'owner' : roleAtLeast(myRole, 'admin'));
  const oidcRemoval = !!removing && (removing.provider === 'google' || removing.provider === 'microsoft' || (identities[removing.id]?.length ?? 0) > 0);
  const changeRole = async (m: Member, role: Role) => {
    setSaving(m.id);
    try {
      await authClient.setRole(m.id, role);
      toast.success(`${m.name ?? m.login} is now ${ROLE_LABEL[role].toLowerCase()}`);
      onChanged();
    } catch (e) {
      toast.error(e);
    } finally {
      setSaving(null);
    }
  };
  /** Every role reads as the same quiet value; a manageable one opens a menu of the others on click. */
  const roleCell = (m: Member, className?: string) =>
    canEdit(m) && saving !== m.id ? (
      <InlineChoice label={`Role of ${m.login}`} value={m.role} options={roleOptions(grantableRoles(myRole))} onChange={(v) => changeRole(m, v)} className={className} />
    ) : (
      <span className={cn('inline-flex h-control-sm items-center px-2', ROLE_VALUE_CLASS, className)}>{ROLE_LABEL[m.role]}</span>
    );
  const columns: Column<Member>[] = [
    {
      key: 'member',
      header: 'Member',
      grow: true,
      text: (m) => `${m.name ?? ''} ${m.login} ${m.email ?? ''} ${(identities[m.id] ?? []).map((i) => i.email ?? '').join(' ')}`,
      sortValue: (m) => (m.name ?? m.login).toLowerCase(),
      cell: (m) => {
        const ids = identities[m.id] ?? [];
        const unverified = m.emailVerified === false && m.provider !== 'github';
        return (
          <span className="flex min-w-0 items-center gap-3">
            <Avatar name={m.name ?? m.login} colorKey={m.login} size={24} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate font-medium text-fg">{m.name ?? m.login}</span>
                {/* One chip per row: an unverified email outranks "You". */}
                {(unverified || m.login === myLogin) && (
                  <Badge tone={unverified ? 'waiting' : 'neutral'} variant={unverified ? 'soft' : 'outline'} icon={unverified ? <CircleAlert /> : undefined} className="shrink-0">
                    {unverified ? (narrow ? 'Unverified' : 'Unverified email') : 'You'}
                  </Badge>
                )}
              </span>
              {/* On a phone the meta line wraps rather than cut an email: the role moves under it. */}
              <span className={cn('flex min-w-0 items-center gap-1 text-caption text-fg-subtle', narrow && 'flex-wrap gap-x-2 gap-y-0')}>
                <span className={narrow ? 'break-all' : 'truncate'}>{memberHandle(m)}</span>
                {narrow ? (
                  <>
                    {/* The Role column folds into the meta line; linked accounts stay on the desktop row. */}
                    {roleCell(m, 'h-auto shrink-0 px-0 text-caption')}
                  </>
                ) : (
                  <IdentityMarks identities={ids} />
                )}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      type: 'badge',
      hideBelow: 'md',
      sortValue: (m) => ROLE_RANK[m.role],
      cell: roleCell,
    },
    { key: 'via', header: 'Access', hideBelow: 'md', text: viaLabel, cell: (m) => <span className="text-sm text-fg-muted">{viaLabel(m)}</span> },
    {
      key: 'seen',
      header: 'Last seen',
      type: 'date',
      hideBelow: 'sm',
      sortValue: (m) => m.lastSeenAt ?? 0,
      cell: (m) => (m.provider === 'owner-link' ? <span className="text-sm text-fg-subtle">Not tracked</span> : <RelativeTime ts={m.lastSeenAt} fallback="Not yet" />),
    },
  ];
  return (
    <Panel title="Members" sub={data.canManage ? `${data.members.length}` : undefined} icon={<Users />} flush>
      <DataTable
        frameless
        label="Members"
        rows={data.members}
        rowKey={(m) => m.id}
        columns={columns}
        searchable={data.members.length > 8 ? { placeholder: 'Search members' } : undefined}
        pageSize={false}
        initialSort={{ key: 'role', dir: 'desc' }}
        empty={<EmptyState icon={<Users />} title="No members yet" description="People appear here after their first sign-in." />}
        rowActions={(m) =>
          !(canEdit(m) || canAddSignIn(m)) ? null : narrow ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton label={`Actions for ${m.login}`} icon={<Ellipsis />} size="sm" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canAddSignIn(m) && (
                  <DropdownMenuItem icon={<Link2 />} onSelect={() => setAdding(m)}>
                    Add a sign-in method
                  </DropdownMenuItem>
                )}
                {canEdit(m) && (
                  <DropdownMenuItem icon={<Trash2 />} danger onSelect={() => setRemoving(m)}>
                    Remove member
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span className="flex items-center gap-1">
              {canAddSignIn(m) && <IconButton label={`Add a sign-in method for ${m.login}`} icon={<Link2 />} size="sm" onClick={() => setAdding(m)} />}
              {canEdit(m) && <IconButton label={`Remove ${m.login}`} icon={<Trash2 />} size="sm" onClick={() => setRemoving(m)} />}
            </span>
          )
        }
      />
      <LinkInviteDialog member={adding} onOpenChange={(v) => !v && setAdding(null)} onCreated={onChanged} />
      {!data.canManage && <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Only admins and owners see everyone and change who can sign in.</p>}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? `Remove ${removing.name ?? removing.login}?` : ''}
        description={
          oidcRemoval
            ? 'They are signed out of every browser now, every token they created is revoked, and their open invites stop working. Their Google and Microsoft accounts are blocked from rejoining until an admin allows them again.'
            : 'They are signed out of every browser now, every token they created is revoked, and their own allow rule is deleted. An organisation rule can still let them sign in again.'
        }
        confirmLabel="Remove member"
        onConfirm={async () => {
          if (!removing) return;
          const r = await authClient.removeMember(removing.id);
          toast.success(
            `${removing.login} was removed`,
            [r.tokensRevoked ? `${r.tokensRevoked} ${r.tokensRevoked === 1 ? 'token was' : 'tokens were'} revoked.` : '', r.stillAllowedVia ? `${r.stillAllowedVia.replace('org:', 'The organisation ')} still lets them in.` : '']
              .filter(Boolean)
              .join(' ') || undefined,
          );
          setRemoving(null);
          onChanged();
        }}
      />
    </Panel>
  );
}

export function MembersView() {
  const me = useMe((s) => s.me);
  // Re-read when the hub says auth changed (state.ext.auth.rev over SSE), instead of polling.
  const rev = useExt<AuthState>('auth')?.rev;
  const { data, error, loading, refresh } = useResource(authClient.members, undefined, rev);
  // Linked accounts, invites and removed people. Optional: an older hub has no /api/auth/sign-in.
  const signIn = useResource<SignInSettings | null>(() => authClient.signInSettings().catch(orNullIfMissing), undefined, rev);
  const myRole: Role = me?.actor.role ?? 'viewer';
  const myLogin = me?.member?.login ?? null;
  const sso = signIn.data;
  const changed = () => {
    void signIn.refresh();
    void refresh().then(() => useMe.getState().load());
  };

  if (loading && !data) {
    return (
      <div className="flex flex-col gap-4" role="status" aria-label="Loading members">
        <SkeletonRows rows={2} />
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (!data) return <ErrorState error={error} onRetry={refresh} />;
  return (
    <div className="flex flex-col gap-4">
      {error != null && <ErrorState variant="inline" error={error} onRetry={refresh} />}
      {signIn.error != null && <ErrorState variant="inline" error={signIn.error} onRetry={signIn.refresh} />}
      <MembersPanel
        data={data}
        identities={sso?.identities ?? {}}
        myRole={myRole}
        myLogin={myLogin}
        canLinkInvite={!!sso?.providers.some((p) => p.id !== 'github' && p.configured)}
        onChanged={changed}
      />
      {sso?.canInvite && <InvitesPanel invites={sso.invites} members={data.members} myRole={myRole} canInvite={sso.canInvite} onChanged={changed} />}
      {sso && data.canManage && roleAtLeast(myRole, 'admin') && <RemovedPanel removed={sso.removed} myRole={myRole} onChanged={changed} />}
      {data.canManage && roleAtLeast(myRole, 'admin') && <RulesPanel data={data} myRole={myRole} members={data.members} onChanged={() => void refresh()} />}
    </div>
  );
}
